import React, { useEffect, useState } from "react";

interface AnalyticsComponentProps {
  GID?: string;
}

const DefaultGID = "G-8W5LR442LD";

const AnalyticsComponent: React.FC<AnalyticsComponentProps> = ({
  GID = DefaultGID,
}: AnalyticsComponentProps) => {
  // State to store Client ID and Session ID
  const [clientID, setClientID] = useState<string>("");
  const [sessionID, setSessionID] = useState<string>("");

  useEffect(() => {
    let isMounted = true;
    async function fetchIDs() {
      // Fetch Client ID and Session ID when the component mounts
      const [clientID, sessionID] = await fetchAnalyticsID(GID, 1000);
      if (isMounted) {
        setClientID(clientID);
        setSessionID(sessionID);
      }
    }
    fetchIDs();
    return () => {
      isMounted = false; // Cleanup: mark component as unmounted when it is unmounted
    };
  }, [GID]); // Run only once on component mount

  return (
    <div>
      <h3>Analytics Information</h3>
      <p>
        <strong>Client ID:</strong> {clientID || "Not available"}
      </p>
      <p>
        <strong>Session ID:</strong> {sessionID || "Not available"}
      </p>
    </div>
  );
};

// This function fetches the Client ID and Session ID from Google Analytics.
/**
 * The fetchAnalyticsID function retrieves both Client ID and Session ID.
 * If either value does not return within 1 second (e.g., due to an ad blocker),
 * the function returns without error, providing empty strings for missing values.
 */
export function fetchAnalyticsID(
  GID: string = DefaultGID,
  timeout: number = 1000
): Promise<[string, string]> {
  if (!window.REACT_APP_ANALYTICS_ENABLED || !window.gtag || typeof window.gtag !== "function") {
    return Promise.resolve(["", ""]); // Return empty strings if gtag is not available
  }
  const timeoutPromise = new Promise<string>((resolve) => {
    setTimeout(() => {
      console.warn("Operation timed out");
      resolve(""); // Resolve with empty string on timeout
    }, timeout);
  });
  const fetchClientID = new Promise<string>((resolve) => {
    if (!window.gtag || typeof window.gtag !== "function") {
      return resolve(""); // Return empty strings if gtag is not available
    }
    // Retrieve the Client ID from Google Analytics
    window.gtag("get", GID, "client_id", (clientID: string) => {
      resolve(clientID);
    });
  });
  const fetchSessionID = new Promise<string>((resolve) => {
    if (!window.gtag || typeof window.gtag !== "function") {
      resolve("");
      return;
    }
    // Retrieve the Session ID from Google Analytics
    window.gtag("get", GID, "session_id", (sessionID: string) => {
      resolve(sessionID);
    });
  });
  return Promise.all([
    Promise.race([fetchClientID, timeoutPromise]),
    Promise.race([fetchSessionID, timeoutPromise]),
  ]);
}

export function getAnalyticsId(): Promise<[string, string]> {
  return fetchAnalyticsID(DefaultGID, 1000);
}

export default AnalyticsComponent;
